/*
 * air-home.js — native behaviour for the converted pulsio-eu homepage.
 * Replaces the Replo runtime for the only interactions the homepage uses:
 *   1. Product carousels (prev/next buttons, dots only — no drag/click-to-slide).
 *   2. Product cards navigate to their product page on click.
 *   3. Sleek-style scroll reveals (CSS transitions + IntersectionObserver).
 * The marquee is pure CSS (@keyframes replo-marquee) and needs no JS.
 * No external dependencies; safe to load once. Idempotent per element.
 */
(function () {
  if (window.__airHomeInit) return;
  window.__airHomeInit = true;

  var TRANSITION = 'transform 450ms cubic-bezier(0.22, 0.61, 0.36, 1)';
  var REVEAL_OPTS = { rootMargin: '0px 0px -8% 0px', threshold: 0.15 };
  var STAGGER_MS = 80;
  var reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function currentX(track) {
    var m = /translate3d\(\s*(-?[\d.]+)px/.exec(track.style.transform || '');
    return m ? parseFloat(m[1]) : 0;
  }

  function productUrl(handle, card) {
    if (card) {
      var explicit = (card.getAttribute('data-replo-product-url') || '').trim();
      if (explicit) return explicit;
      var linked = card.querySelector(
        'a.air-product-card-media-link[href], a[href*="/products/"][href]'
      );
      if (linked) return linked.getAttribute('href');
    }
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    return root + 'products/' + handle;
  }

  function wireProductCardLinks(card, handle) {
    if (card.__airLinks) return;
    card.__airLinks = true;
    var url = productUrl(handle, card);
    var picture = card.querySelector('picture');
    if (picture && !picture.closest('a[href]')) {
      var link = document.createElement('a');
      link.href = url;
      link.className = 'air-product-card-media-link';
      var label = (card.textContent || '').replace(/\s+/g, ' ').trim();
      if (label) link.setAttribute('aria-label', label.slice(0, 120));
      picture.parentNode.insertBefore(link, picture);
      link.appendChild(picture);
    }
    Array.prototype.forEach.call(
      card.querySelectorAll('a[role="button"]:not([href])'),
      function (cta) {
        cta.href = url;
      }
    );
  }

  function initCarousel(root) {
    if (window.AirCarousel && window.AirCarousel.init) {
      // Homepage sliders (Best Sellers) are arrows-only by design: drag-to-slide
      // was removed to stop image clicks swiping the product-card carousel.
      return window.AirCarousel.init(root, { swipe: false, loop: true });
    }
  }

  function hasAlchemyPaused(el) {
    var cs = getComputedStyle(el);
    return (
      cs.animationName &&
      cs.animationName.indexOf('alchemy') !== -1 &&
      cs.animationPlayState === 'paused'
    );
  }

  function hasBackgroundImage(el) {
    if ((el.style.backgroundImage || '').indexOf('url') !== -1) return true;
    var cs = getComputedStyle(el);
    return cs.backgroundImage && cs.backgroundImage !== 'none';
  }

  function isInView(el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.top < vh * 0.92 && r.bottom > 0;
  }

  function revealEl(el) {
    if (el.__airRevealed) return;
    el.__airRevealed = true;
    el.classList.remove('air-reveal-pending');
    el.classList.add('air-reveal-visible');
  }

  function tagReveal(el, variant, delayMs) {
    if (el.__airRevealTagged) return;
    el.__airRevealTagged = true;
    el.setAttribute('data-air-reveal', variant || 'fade-up');
    if (delayMs) {
      el.style.setProperty('--air-reveal-delay', delayMs + 'ms');
    }
    el.classList.add('air-reveal-pending', 'air-reveal--' + (variant || 'fade-up'));
    if (reducedMotion || isInView(el)) {
      revealEl(el);
      return null;
    }
    return el;
  }

  function initAirMotion() {
    var toObserve = [];
    var alchemyBg = [];

    function observe(el) {
      if (el && !el.__airRevealed) toObserve.push(el);
    }

    // Explicit data-air-reveal markers (if added in Liquid later)
    document.querySelectorAll('[data-air-reveal]').forEach(function (el) {
      if (el.__airRevealTagged) return;
      var variant = el.getAttribute('data-air-reveal') || 'fade-up';
      var delay = parseInt(el.getAttribute('data-air-reveal-delay') || '0', 10);
      observe(tagReveal(el, variant, delay));
    });

    document.querySelectorAll('[id^="replo-section-"]').forEach(function (section) {
      // Carousels: reveal the container, not individual slides
      section.querySelectorAll('[data-replo-carousel]').forEach(function (carousel) {
        if (hasAlchemyPaused(carousel) && hasBackgroundImage(carousel)) {
          alchemyBg.push(carousel);
          return;
        }
        observe(tagReveal(carousel, 'fade-up', 0));
      });

      // Product tiles outside carousels: staggered fade-up
      var gridIndex = 0;
      section.querySelectorAll('[data-replo-product-handle]').forEach(function (card) {
        if (card.closest('[data-replo-carousel]')) return;
        if (hasAlchemyPaused(card)) return;
        var parent = card.parentElement;
        if (parent && hasAlchemyPaused(parent)) return;
        observe(tagReveal(card, 'fade-up', gridIndex * STAGGER_MS));
        gridIndex++;
      });

      // Trust badges / widgets
      section.querySelectorAll('.trustpilot-widget').forEach(function (widget) {
        var wrap = widget.parentElement;
        if (!wrap || wrap.__airRevealTagged) return;
        observe(tagReveal(wrap, 'fade', 0));
      });
    });

    // Replo alchemy-fade blocks → Sleek-style CSS reveal (skip hero backgrounds)
    var nodes = document.querySelectorAll('[data-rid]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.__airRevealTagged || node.closest('[data-replo-carousel]')) continue;
      if (!hasAlchemyPaused(node)) continue;

      if (hasBackgroundImage(node)) {
        alchemyBg.push(node);
        continue;
      }

      node.classList.add('air-reveal--alchemy-override');
      observe(tagReveal(node, 'fade-up', 0));
    }

    function playAlchemy(el) {
      el.style.animationPlayState = 'running';
    }

    if (alchemyBg.length) {
      if (!('IntersectionObserver' in window) || reducedMotion) {
        alchemyBg.forEach(playAlchemy);
      } else {
        var alchemyIo = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (e) {
              if (e.isIntersecting) {
                playAlchemy(e.target);
                alchemyIo.unobserve(e.target);
              }
            });
          },
          { rootMargin: '0px 0px -5% 0px', threshold: 0.01 }
        );
        alchemyBg.forEach(function (el) {
          if (isInView(el)) playAlchemy(el);
          else alchemyIo.observe(el);
        });
      }
    }

    if (!toObserve.length) return;

    if (reducedMotion) {
      toObserve.forEach(revealEl);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      toObserve.forEach(revealEl);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            revealEl(e.target);
            io.unobserve(e.target);
          }
        });
      },
      REVEAL_OPTS
    );

    toObserve.forEach(function (el) {
      if (isInView(el)) revealEl(el);
      else io.observe(el);
    });
  }

  function initProductNav(scope) {
    var cards = (scope || document).querySelectorAll(
      '[data-replo-product-handle]'
    );
    Array.prototype.forEach.call(cards, function (card) {
      if (card.__airNav) return;
      var handle = (card.getAttribute('data-replo-product-handle') || '').trim();
      if (!handle || handle === 'empty-product') return;
      card.__airNav = true;
      wireProductCardLinks(card, handle);
      card.style.cursor = 'pointer';
      card.addEventListener('click', function (e) {
        if (
          e.target.closest(
            'a[href], button, [data-replo-part="nav-button"], [aria-label^="Go to slide"]'
          )
        ) {
          return;
        }
        window.location.href = productUrl(handle, card);
      });
    });
  }

  function patchReploMoneyFormat() {
    var settings = window.themeVariables && window.themeVariables.settings;
    if (!settings || !settings.moneyFormat) return;
    var fmt = settings.moneyFormat;
    var fmtCur = settings.moneyWithCurrencyFormat || fmt;
    document
      .querySelectorAll('script#replo-deps-shopify-store')
      .forEach(function (node) {
        try {
          var data = JSON.parse(node.textContent);
          if (!data.shop) return;
          data.shop.moneyFormat = fmt;
          data.shop.moneyWithCurrencyFormat = fmtCur;
          node.textContent = JSON.stringify(data);
        } catch (e) {}
      });
    if (window.Globo) window.Globo.moneyFormat = fmt;
  }

  ready(function () {
    patchReploMoneyFormat();
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-replo-carousel]'),
      initCarousel
    );
    initProductNav(document);
    initAirMotion();
  });
})();
